import { useParams, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { MarketplacePage } from './marketplace'

const API_BASE = '/api/v1'

interface CityInfo {
  name: string
  slug: string
  roomCount: number
}

/**
 * City landing page - thin wrapper around MarketplacePage
 * Fetches city name and passes preset filter to the main marketplace component
 */
export function MarketplaceCityPage() {
  const { citySlug } = useParams({ from: '/marketplace/city/$citySlug' })
  const navigate = useNavigate()
  const [cityInfo, setCityInfo] = useState<CityInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    loadCityInfo()
  }, [citySlug])

  const loadCityInfo = async () => {
    try {
      setIsLoading(true)
      setError(false)
      
      // Fetch city info to get proper name
      const response = await fetch(`${API_BASE}/marketplace/cities/${citySlug}`)
      if (!response.ok) {
        if (response.status === 404) {
          setError(true)
          return
        }
        throw new Error('Failed to load city')
      }
      
      const data = await response.json()
      setCityInfo({
        name: data.city.name,
        slug: data.city.slug,
        roomCount: data.city.roomCount
      })
    } catch (err) {
      console.error('Failed to load city:', err)
      setError(true)
    } finally {
      setIsLoading(false)
    }
  }

  // Handle city not found - redirect to marketplace
  useEffect(() => {
    if (!isLoading && error) {
      navigate({ to: '/marketplace' })
    }
  }, [isLoading, error, navigate])

  // Handle loading state with a simple spinner
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  // Handle city not found - show message while redirecting
  if (error || !cityInfo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">City not found. Redirecting...</p>
      </div>
    )
  }

  // Render marketplace with city preset
  return (
    <MarketplacePage 
      presetCity={cityInfo.name}
      cityDisplayName={cityInfo.name}
    />
  )
}
